import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCardContext } from '../contexts/CardContext';
import { AuthService } from '../services/authService';
import { ApiService } from '../services/apiService';
import BiometricSetup from '../components/BiometricSetup';
import SecuritySettings from '../components/SecuritySettings';
import EmailValidation from '../components/EmailValidation';
import { authenticateWithBiometric } from '../utils/biometricAuth';
import { generateDeviceSignature } from '../utils/deviceSignature';
import { AppHeader } from '../components/AppHeader';
import './BalanceDisplay.css';
import { CardStateService } from '../services/cardStateService';
import { CardData } from '../types/cardTypes';
// @mcc-dev-preview:import
import { resolveDevState } from '../dev/devPreview';
// @mcc-dev-preview:import
import {
  DEV_PREVIEW_BALANCE_CARD_DETAILS,
  DEV_PREVIEW_BIOMETRIC_METHODS,
  DEV_PREVIEW_USER,
} from '../dev/devPreviewMocks';

// @mcc-dev-preview:start dev-preview-types
type BalanceDisplayDevState =
  | 'loading'
  | 'ready'
  | 'error'
  | 'login-modal'
  | 'biometric-setup'
  | 'biometric-management';

const BALANCE_DISPLAY_DEV_STATES: BalanceDisplayDevState[] = [
  'loading',
  'ready',
  'error',
  'login-modal',
  'biometric-setup',
  'biometric-management',
];
// @mcc-dev-preview:end dev-preview-types

interface CardDetails {
  cardId: string;
  coin: string;
  coinSymbol: string;
  balance: string;
  balanceAUD: string;
  originalAmount: string;
  dollarChange: string;
  percentChange: string;
  userName: string;
}

// Using the imported CardData type from cardTypes.ts

interface CryptoRate {
  id: string;
  symbol: string;
  buyPrice: number;
  sellPrice: number;
  currency: string;
  lastUpdated: string;
  source?: string;
}

interface UserData {
  firstName: string | null;
  status: string;
}

const BalanceDisplay: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const devState = resolveDevState(searchParams, BALANCE_DISPLAY_DEV_STATES, [
    {
      when: (p) => p.get('showLoginModal') === '1' || p.get('showLoginModal') === 'true',
      state: 'login-modal',
    },
  ]);
  const { cardData, fetchCardData } = useCardContext();
  const [cardDetails, setCardDetails] = useState<CardDetails | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Biometric authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [biometricStatus, setBiometricStatus] = useState<any>(null);
  const [biometricSetupComplete, setBiometricSetupComplete] = useState(false);


  // Authentication modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string>('');

  // Biometric management state
  const [showBiometricManagement, setShowBiometricManagement] = useState(false);
  const [biometricMethods, setBiometricMethods] = useState<any[]>([]);
  const [isLoadingBiometrics, setIsLoadingBiometrics] = useState(false);
  
  // Biometric removal authentication state
  const [showBiometricRemovalAuth, setShowBiometricRemovalAuth] = useState(false);
  const [signingMethodToRemove, setSigningMethodToRemove] = useState<string | null>(null);
  const [deviceToRemoveName, setDeviceToRemoveName] = useState<string | null>(null);
  const [removalAuthMethod, setRemovalAuthMethod] = useState<'pin' | 'biometric' | null>(null);
  const [removalPin, setRemovalPin] = useState('');
  const [isRemovingBiometric, setIsRemovingBiometric] = useState(false);

  const getChangeClass = (changeValue: string): 'positive' | 'negative' => {
    // cardDetails.dollarChange and cardDetails.percentChange are formatted with leading '+'/'-'
    // (e.g. "+12.34" or "-5.67"), so we can derive the sign from the first character.
    return changeValue.trim().startsWith('-') ? 'negative' : 'positive';
  };

  const checkBiometricStatus = useCallback(async () => {
    try {
      const cardId = searchParams.get('cardId');
      if (!cardId) return;
      
      const response = await ApiService.checkBiometricStatus(cardId);
      if (response.success) {
        setBiometricStatus(response.data);
      }
    } catch (error) {
      console.error('Error checking biometric status:', error);
    }
  }, [searchParams]);

  useEffect(() => {
    if (devState) return;

    const cardId = searchParams.get('cardId');
    
    // Always fetch fresh card data from backend
    if (cardId) {
      fetchCardData(cardId);
    }
  }, [searchParams, fetchCardData, devState]); // Added fetchCardData dependency back

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = AuthService.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        checkBiometricStatus();
      }
    };
    
    checkAuth();
  }, [checkBiometricStatus]);

  // @mcc-dev-preview:start dev-preview-seed
  useEffect(() => {
    if (!devState) return;

    const mock = DEV_PREVIEW_BALANCE_CARD_DETAILS;
    const balanceAUDNum = parseFloat(mock.balanceAUD);
    const originalAmountNum = parseFloat(mock.originalAmount);
    const dollarChangeValue = balanceAUDNum - originalAmountNum;
    const percentChangeValue = (dollarChangeValue / originalAmountNum) * 100;
    const dollarChangeSign = dollarChangeValue >= 0 ? '+' : '-';
    const percentChangeSign = percentChangeValue >= 0 ? '+' : '-';

    const previewCardDetails: CardDetails = {
      cardId: mock.cardId,
      coin: mock.coin,
      coinSymbol: mock.coinSymbol,
      balance: `${mock.balance} ${mock.coinSymbol}`,
      balanceAUD: `$${mock.balanceAUD}AUD`,
      originalAmount: `$${mock.originalAmountAUD}AUD`,
      dollarChange: `${dollarChangeSign}$${Math.abs(dollarChangeValue).toFixed(2)}AUD`,
      percentChange: `${percentChangeSign}${Math.abs(percentChangeValue).toFixed(2)}%`,
      userName: DEV_PREVIEW_USER.firstName || 'User',
    };

    if (devState === 'loading') {
      setIsLoading(true);
      return;
    }

    setUserData({ firstName: DEV_PREVIEW_USER.firstName, status: 'active' });

    if (devState === 'error') {
      setIsLoading(false);
      setCardDetails(null);
      setError(searchParams.get('errorMessage') ?? 'Failed to fetch card data');
      return;
    }

    setCardDetails(previewCardDetails);
    setIsLoading(false);

    switch (devState) {
      case 'login-modal':
        setShowAuthModal(true);
        break;
      case 'biometric-setup':
        setShowBiometricSetup(true);
        break;
      case 'biometric-management':
        setShowBiometricManagement(true);
        setBiometricMethods(DEV_PREVIEW_BIOMETRIC_METHODS);
        break;
      case 'ready':
      default:
        break;
    }
  }, [devState, searchParams]);
  // @mcc-dev-preview:end dev-preview-seed

  const handleBiometricSuccess = () => {
    setBiometricSetupComplete(true);
    setShowBiometricSetup(false);
    // Refresh biometric status
    checkBiometricStatus();
  };

  const handleBiometricSkip = () => {
    setShowBiometricSetup(false);
  };

  // Authentication handlers for email validation
  const handleEmailValidationSuccess = async (email: string) => {
    if (devState) return;

    try {
      setIsSendingEmail(true);
      setEmailError('');

      const cardId = searchParams.get('cardId');
      if (!cardId) {
        setEmailError('Card ID is required for authentication');
        setIsSendingEmail(false);
        return;
      }

      // First, handle the user (create or get existing)
      await ApiService.handleEmailUser(email);

      // Get current path for returnTo parameter
      const currentPath = window.location.pathname + window.location.search;

      // Send validation email
      const response = await ApiService.sendValidationEmail(email, cardId, currentPath);

      if (response.success) {
        // Close modal and redirect to email-sent page
        setShowAuthModal(false);
        navigate('/email-sent', {
          state: {
            email: email,
            cardId: cardId,
            returnTo: currentPath,
            isValidationEmail: true
          }
        });
      } else {
        setEmailError('Failed to send validation email. Please try again.');
      }
    } catch (error: any) {
      console.error('❌ BalanceDisplay: Error sending validation email:', error);
      setEmailError(error.message || 'Network error. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleEmailValidationError = (errorMessage: string) => {
    console.error('❌ BalanceDisplay: Email validation error:', errorMessage);
    setEmailError(errorMessage);
  };

  useEffect(() => {
    if (devState) return;

    const fetchCardDetails = async () => {
      try {
        const cardId = searchParams.get('cardId');
        if (!cardId) {
          const errorMessage = 'Card ID is required';
          navigate(CardStateService.getErrorRoute(errorMessage));
          return;
        }

        // Always fetch fresh card data from API
        let cardResponse = await ApiService.getCardByCardId(cardId);

        if (!cardResponse) {
          const errorMessage = 'Failed to fetch card data';
          navigate(CardStateService.getErrorRoute(errorMessage, cardId));
          return;
        }

        const cardDataObj = cardResponse as CardData;
        
        // Fetch user data for the card
        const userResponse = await ApiService.getUserForCard(cardId);
        
        if (userResponse.success && userResponse.data.user) {
          setUserData(userResponse.data.user);
        } else {
          const errorMessage = 'No user found for this card';
          navigate(CardStateService.getErrorRoute(errorMessage, cardId));
          return;
        }
        
        // Check if user is a guest (no actual user linked)
        if (userResponse.data.user.status === 'guest') {
          const errorMessage = 'No user associated with this card';
          navigate(CardStateService.getErrorRoute(errorMessage, cardId));
          return;
        }
        
        // Fetch crypto rates
        const cryptoRates = await ApiService.getCryptoRatesList();
        
        // Derive coinSymbol from coin if not provided
        const coinSymbol = cardDataObj.coinSymbol || (cardDataObj.coin === 'Bitcoin' ? 'BTC' : cardDataObj.coin);
        
        // Get the coin rate by finding the matching symbol using the derived coinSymbol
        const coinRate = cryptoRates.find((rate: CryptoRate) => rate.symbol === coinSymbol);
        if (!coinRate) {
          const errorMessage = `No rate found for ${coinSymbol}`;
          navigate(CardStateService.getErrorRoute(errorMessage, cardId));
          return;
        }
        
        // Use sellPrice for calculating AUD balance (what the user would get if they sold)
        const balanceNum = parseFloat(cardDataObj.balance.toString());
        const balanceAUD = (balanceNum * coinRate.sellPrice).toFixed(2);
        const originalAmountNum = parseFloat(cardDataObj.originalAmount);
        const originalAmountAUD = originalAmountNum.toFixed(2);
        
        // Calculate changes using real rates
        const dollarChangeValue = parseFloat(balanceAUD) - originalAmountNum;
        const percentChangeValue = (dollarChangeValue / originalAmountNum) * 100;
        
        const dollarChange = Math.abs(dollarChangeValue).toFixed(2);
        const percentChange = Math.abs(percentChangeValue).toFixed(2);
        
        // Determine the sign based on the actual value
        const dollarChangeSign = dollarChangeValue >= 0 ? '+' : '-';
        const percentChangeSign = percentChangeValue >= 0 ? '+' : '-';
        
        // Get user name from fetched user data
        const userName = userResponse.data.user.firstName || 'User';
        
        const cardDetails: CardDetails = {
          cardId: cardDataObj.cardId,
          coin: cardDataObj.coin,
          coinSymbol: coinSymbol,
          balance: `${cardDataObj.balance} ${coinSymbol}`,
          balanceAUD: `$${balanceAUD}AUD`,
          originalAmount: `$${originalAmountAUD}AUD`,
          dollarChange: `${dollarChangeSign}$${dollarChange}AUD`,
          percentChange: `${percentChangeSign}${percentChange}%`,
          userName: userName
        };
        
        setCardDetails(cardDetails);
        setIsLoading(false);
      } catch (error) {
        console.error('❌ BalanceDisplay: Error fetching card data:', error);
        const cardId = searchParams.get('cardId');
        const errorMessage = 'Failed to fetch card data';
        navigate(CardStateService.getErrorRoute(errorMessage, cardId || undefined));
      }
    };

    fetchCardDetails();
  }, [searchParams, navigate, cardData, devState]);

  const handleOptionsButtonClick = () => {
    if (!isAuthenticated) {
      // Show authentication modal instead of redirecting
      setShowAuthModal(true);
      return;
    }
    
    handleSellCrypto();
  };

  const handleSellCrypto = () => {
    // Navigate to sell page with cardId
    const cardId = searchParams.get('cardId');
    if (cardId) {
      window.location.href = `/card-sell?cardId=${cardId}`;
    }
  };

  const loadBiometricMethods = async () => {
    if (!isAuthenticated) return;
    
    setIsLoadingBiometrics(true);
    try {
      const cardId = searchParams.get('cardId');
      if (!cardId) return;
      
      const response = await ApiService.getCardBiometricMethods(cardId);
      if (response.success) {
        setBiometricMethods(response.data || []);
      }
    } catch (error) {
      console.error('Error loading biometric methods:', error);
    } finally {
      setIsLoadingBiometrics(false);
    }
  };

  const handleRemoveBiometric = async (signingMethodId: string, deviceName?: string) => {
    if (!isAuthenticated) {
      return;
    }
    
    // Close the management modal first, then open the removal modal
    setShowBiometricManagement(false);
    setSigningMethodToRemove(signingMethodId);
    setDeviceToRemoveName(deviceName || null);
    setShowBiometricRemovalAuth(true);
  };

  const handleBiometricRemovalAuth = async (authMethod: 'pin' | 'biometric') => {
    if (!signingMethodToRemove) return;
    
    setRemovalAuthMethod(authMethod);
    
    if (authMethod === 'pin') {
      // Show PIN input for PIN authentication
      setRemovalPin('');
    } else {
      // Try to authenticate with existing biometrics
      await authenticateAndRemoveBiometric();
    }
  };

  const handlePinSubmitForRemoval = async () => {
    if (!signingMethodToRemove || removalPin.length !== 6) return;
    
    setIsRemovingBiometric(true);
    try {
      const cardId = searchParams.get('cardId');
      if (!cardId) return;
      
      // signingMethodToRemove contains the signingMethodId
      const response = await ApiService.removeBiometricDevice(cardId, signingMethodToRemove, removalPin);
      if (response.success) {
        // Refresh biometric methods and status
        await loadBiometricMethods();
        const statusResponse = await ApiService.checkBiometricStatus(cardId);
        if (statusResponse.success) {
          setBiometricStatus(statusResponse.data);
        }
        // Close modal and reset state
        setShowBiometricRemovalAuth(false);
        setSigningMethodToRemove(null);
        setDeviceToRemoveName(null);
        setRemovalAuthMethod(null);
        setRemovalPin('');
        setError(null);
        

      }
    } catch (error: any) {
      console.error('Error removing biometric device:', error);
      // Show error to user
      setError(error.message || 'Failed to remove biometric device');
    } finally {
      setIsRemovingBiometric(false);
    }
  };

  const authenticateAndRemoveBiometric = async () => {
    if (!signingMethodToRemove) return;
    
    setIsRemovingBiometric(true);
    try {
      const authenticationResult = await authenticateWithExistingBiometric();
      
      if (authenticationResult.success) {
        const cardId = searchParams.get('cardId');
        if (!cardId) return;
        
        if (!authenticationResult.signingMethodId || !authenticationResult.signingMethodValue) {
          setError('Biometric authentication failed. Please use PIN instead.');
          setRemovalAuthMethod('pin');
          setRemovalPin('');
          return;
        }
        
        const response = await ApiService.removeBiometricDevice(
          cardId, 
          signingMethodToRemove, // signingMethodToRemove contains the signingMethodId
          undefined, // No PIN
          {
            signingMethodId: authenticationResult.signingMethodId,
            signingMethodValue: authenticationResult.signingMethodValue
          }
        );
        
        if (response.success) {
          // Refresh biometric methods and status
          await loadBiometricMethods();
          const statusResponse = await ApiService.checkBiometricStatus(cardId);
          if (statusResponse.success) {
            setBiometricStatus(statusResponse.data);
          }
                  // Close modal and reset state
        setShowBiometricRemovalAuth(false);
        setSigningMethodToRemove(null);
        setDeviceToRemoveName(null);
        setRemovalAuthMethod(null);
        setRemovalPin('');
        setError(null);
        } else {
          setError(response.message || 'Failed to remove biometric device');
        }
      } else {
        // Check if the failure is because no matching device was found
        if (authenticationResult.reason === 'no_device_match') {
          setError('No biometric authentication found for this device. Please use your PIN to remove this device.');
        } else {
          setError('Biometric authentication failed. Please use PIN instead.');
        }
        
        setRemovalAuthMethod('pin');
        setRemovalPin('');
      }
    } catch (error: any) {
      console.error('❌ BalanceDisplay: Biometric authentication failed:', error);
      
      // Check if this is a temporary lock error from Venly
      if (error.message && error.message.includes('temporarily locked')) {
        setError(error.message); // Show the actual lock error message
      } else {
        // For other errors, fall back to PIN
        setError('Biometric authentication failed. Please use PIN instead.');
        setRemovalAuthMethod('pin');
        setRemovalPin('');
      }
    } finally {
      setIsRemovingBiometric(false);
    }
  };

  const authenticateWithExistingBiometric = async (): Promise<{ success: boolean; signingMethodId?: string; signingMethodValue?: string; reason?: string }> => {
    try {
      // Check if there's a biometric method for this device
      const deviceSignature = await generateDeviceSignature();
      const cardId = searchParams.get('cardId');
      if (!cardId) return { success: false };
      
      const biometricMethodsResponse = await ApiService.getCardBiometricMethods(cardId);
      
      if (biometricMethodsResponse.success && biometricMethodsResponse.data) {
        const biometricMethods = biometricMethodsResponse.data;
        
        if (biometricMethods.length === 0) {
          return { success: false };
        }
        
        const availableBiometric = biometricMethods.find((method: any) => 
          method.deviceSignature === deviceSignature
        );
        
        if (!availableBiometric) {
          return { 
            success: false,
            reason: 'no_device_match' // Indicate that no matching device was found
          };
        }
        
        if (availableBiometric && availableBiometric.signingMethodId) {
          // Use the shared utility function for biometric authentication
          // WebAuthn will return the credential.id which matches what was stored in Venly
          const authResult = await authenticateWithBiometric();
          
          if (authResult.success && authResult.credentialId) {
            // Return the authentication data needed for the backend
            // Venly expects the credentialId (the same value stored during creation), not the signature
            return {
              success: true,
              signingMethodId: availableBiometric.signingMethodId,
              signingMethodValue: authResult.credentialId // Use credentialId, not signature
            };
          }
        }
      }
      
      return { success: false };
    } catch (error) {
      console.error('❌ BalanceDisplay: Error during biometric authentication:', error);
      return { success: false };
    }
  };

  // Device signature generation is now handled by shared utility
  // generateDeviceSignature is imported from deviceSignature.ts

  if (isLoading) {
    return (
      <div className="balance-display-page ev-page ev-page-enter">
        <AppHeader />
        <main className="balance-content">
          <div className="balance-loading-spinner">
            <div className="balance-spinner"></div>
            <p>Loading your card details...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!cardDetails) {
    return (
      <div className="balance-display-page ev-page ev-page-enter">
        <AppHeader />
        <main className="balance-content">
          <div className="error-message">
            <h1>Oops!</h1>
            <p>{error || 'Unable to load card details. Please try again.'}</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="balance-display-page ev-page ev-page-enter" data-testid="balance-display-page">
      {showBiometricSetup ? (
        <BiometricSetup 
          cardId={searchParams.get('cardId')!}
          onSuccess={handleBiometricSuccess}
          onSkip={handleBiometricSkip}
        />
      ) : showBiometricManagement ? (
        <div className="biometric-management-modal-overlay">
          <div className="biometric-management-modal">
            <div className="biometric-management-header">
              <h3>🔐 Manage Biometric Authentication</h3>
              <p>View and manage your registered biometric devices</p>
            </div>

            {isLoadingBiometrics ? (
              <div className="biometric-loading">
                <div className="loading-spinner-small">
                  <div className="spinner"></div>
                </div>
                <span>Loading biometric devices...</span>
              </div>
            ) : biometricMethods.length > 0 ? (
              <div className="biometric-methods-list">
                {biometricMethods.map((method, index) => (
                  <div key={index} className="biometric-method-item">
                    <div className="method-info">
                      <span className="method-icon">
                        {method.deviceType === 'fingerprint' ? '👆' : 
                         method.deviceType === 'faceid' ? '👁️' : '🔐'}
                      </span>
                      <div className="method-details">
                        <span className="method-type">
                          {method.deviceType === 'fingerprint' ? 'Fingerprint' :
                           method.deviceType === 'faceid' ? 'Face ID' :
                           method.deviceType === 'touchid' ? 'Touch ID' : 'Biometric'}
                        </span>
                        <span className="method-device-id">{method.deviceName || method.deviceId || 'Unknown Device'}</span>
                        <span className="method-platform">{method.platform}</span>
                        <span className="method-date">
                          Added: {new Date(method.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <button
                      className="remove-method-btn"
                      onClick={() => handleRemoveBiometric(method.signingMethodId, method.deviceName || method.deviceId || undefined)}
                      title="Remove this device"
                    >
                      🗑️ Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-biometric-methods">
                <p>No biometric devices registered.</p>
              </div>
            )}

            <div className="biometric-management-actions">
              <button
                className="add-new-device-btn"
                onClick={() => {
                  setShowBiometricManagement(false);
                  setShowBiometricSetup(true);
                }}
              >
                ➕ Add New Device
              </button>
              <button
                className="close-modal-btn"
                onClick={() => setShowBiometricManagement(false)}
              >
                ✕ Close
              </button>
            </div>
          </div>
        </div>
      ) : showAuthModal ? (
        <div className="auth-modal-overlay">
          <div className="auth-modal">
            <div className="auth-modal-header">
              <h2>Login Required</h2>
              <p>Please enter your email address to start the sale process.</p>
            </div>
            
            {emailError && (
              <div className="error-message" style={{ marginBottom: '1rem', padding: '0.75rem', background: 'transparent', border: 'none', borderRadius: '6px', color: '#c33' }}>
                {emailError}
              </div>
            )}
            
            <EmailValidation
              onValidationSuccess={handleEmailValidationSuccess}
              onValidationError={handleEmailValidationError}
              isLoading={isSendingEmail}
              disabled={isSendingEmail}
              showDisclaimer={true}
              disclaimerText="By entering your email address, you agree to receive transactional emails for account access."
            />
            
            <button
              onClick={() => {
                setShowAuthModal(false);
                setEmailError('');
              }}
              className="back-button"
              style={{ marginTop: '1rem' }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <AppHeader />
          {/* Welcome Section */}
          <div className="welcome-section">
            <span className="welcome-message">Welcome back {cardDetails.userName}</span>
          </div>

          {/* Main Content */}
          <main className="balance-content">
            <h1 className="page-title">YOUR CARD DETAILS</h1>
            
            <div className="card-details-box">
              <div className="detail-row">
                <span className="detail-label">Balance:</span>
                <span className="detail-value balance-value">
                  <span className="balance-coin-amount">{cardDetails.balance}</span>
                  <span className="balance-aud-amount">{cardDetails.balanceAUD}</span>
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Coin:</span>
                <span className="detail-value">{cardDetails.coin}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Card ID:</span>
                <span className="detail-value">{cardDetails.cardId}</span>
              </div>
            </div>

            <div className="purchase-info">
              <p>Original purchase amount was: {cardDetails.originalAmount}</p>
            </div>

            <div className="change-info">
              <div className="change-row">
                <div className="change-item">
                  <span className="change-label">$ Change</span>
                  <span className={`change-value ${getChangeClass(cardDetails.dollarChange)}`}>{cardDetails.dollarChange}</span>
                </div>
                <div className="change-item">
                  <span className="change-label">% Change</span>
                  <span className={`change-value ${getChangeClass(cardDetails.percentChange)}`}>{cardDetails.percentChange}</span>
                </div>
              </div>
            </div>

            {/* Security Settings - Only visible when authenticated */}
            {isAuthenticated && (
              <SecuritySettings
                biometricStatus={biometricStatus}
                onOpenBiometricSetup={() => setShowBiometricSetup(true)}
              />
            )}

            {/* Sell My Crypto Button */}
            <div className="sell-my-crypto-container">
              <button 
                className={`submit-button ${isAuthenticated ? 'authenticated' : 'login-required'}`}
                onClick={handleOptionsButtonClick}
              >
                <span className="sell-my-crypto-button-text">Sell crypto NOW</span>
              </button>
            </div>
          </main>

          {/* Footer */}
          <footer className="balance-footer">
            <div className="footer-content">
              <p>© My Crypto Card Pty Ltd 2025. All rights reserved.</p>
              <p>My Crypto Card Pty Ltd ACN 679 646 390 is a registered Digital Currency Exchange (DCE) provider with AUSTRAC - DCE100876698-001.</p>
              <p className="disclaimer">
                Nothing on this Site constitutes investment-, legal-, accounting- or tax advice, or a representation that any investment or strategy is suitable or appropriate for individual circumstances, or otherwise constitutes a personal recommendation for any specific investor. We recommend that investors independently assess, with a professional advisor, the specific financial risks as well as legal-, regulatory-, credit-, tax- and accounting consequences.
              </p>
            </div>
          </footer>



          {/* Biometric Removal Authentication Modal */}
          {showBiometricRemovalAuth && (
            <div className="pin-modal-overlay">
              <div className="pin-modal">
                <div className="pin-modal-header">
                  <h3>🔐 Remove Biometric Device</h3>
                  <p>Please authenticate to remove this biometric device</p>
                  {deviceToRemoveName && (
                    <p style={{fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)', marginTop: '0.5rem'}}>
                      Device: <strong>{deviceToRemoveName}</strong>
                    </p>
                  )}
                </div>
                
                {!removalAuthMethod ? (
                  <div className="auth-choice-container">
                    <button 
                      onClick={() => handleBiometricRemovalAuth('biometric')}
                      className="auth-choice-btn biometric-choice"
                      disabled={isRemovingBiometric}
                    >
                      <span className="auth-choice-icon">👆</span>
                      <span className="auth-choice-text">
                        <strong>Use Existing Biometric</strong>
                        <small>Authenticate with your current biometric device</small>
                      </span>
                    </button>
                    
                    <button 
                      onClick={() => handleBiometricRemovalAuth('pin')}
                      className="auth-choice-btn pin-choice"
                      disabled={isRemovingBiometric}
                    >
                      <span className="auth-choice-icon">🔢</span>
                      <span className="auth-choice-text">
                        <strong>Use PIN</strong>
                        <small>Enter your 6-digit card PIN</small>
                      </span>
                    </button>
                  </div>
                ) : removalAuthMethod === 'pin' ? (
                  <div className="pin-input-container">
                    <input
                      type="password"
                      value={removalPin}
                      onChange={(e) => setRemovalPin(e.target.value)}
                      placeholder="Enter 6-digit PIN"
                      maxLength={6}
                      className="pin-input"
                      autoFocus
                    />
                  </div>
                ) : null}

                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}

                <div className="pin-modal-actions">
                  {removalAuthMethod === 'pin' && (
                    <button 
                      onClick={handlePinSubmitForRemoval}
                      disabled={isRemovingBiometric || removalPin.length !== 6}
                      className="pin-submit-btn"
                    >
                      {isRemovingBiometric ? 'Removing...' : 'Remove Device'}
                    </button>
                  )}
                  
                  <button 
                    onClick={() => {
                      setShowBiometricRemovalAuth(false);
                      setSigningMethodToRemove(null);
                      setRemovalAuthMethod(null);
                      setRemovalPin('');
                      setError(null);
                    }}
                    className="pin-cancel-btn"
                    disabled={isRemovingBiometric}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BalanceDisplay; 